﻿import { useState } from "react";
import { useToasts } from "react-toast-notifications";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { QrCode, Edit, Trash, FileText, Truck } from "lucide-react";
import printJS from "print-js";

import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import BasicConfirm from "@/components/Confirm/BasicConfirm";

import { hasPermission } from "@/services/Utils";
import { usePrintCodes } from "@/hooks/inventories/useInventories";
import { base64toBlob } from "@/hooks/printer/useBase64";

import { textConstants } from "@/languages/textConstants";
import { useOrderDeliveryPaginate, useOrderDeliveryDelete, useOrderDeliveryConfirmAssign, useOrderDeliveryTransform } from "@/hooks/purchases/orders_delivery";
import OrdersDeliveryAssign from "../OrdersDeliveryAssign";

const OrdersDeliveryActionItem = ({ item, indice, data, base }) => {
	let location = useLocation();
	const { addToast } = useToasts();

	const { customer } = useSelector((state) => state.auth.user);

	const [showDelete, setShowDelete] = useState(false);
	const [showAssign, setShowAssign] = useState(false);
	const [showAssignSale, setShowAssignSale] = useState(false);
	const [showAssignDelivery, setShowAssignDelivery] = useState(false);

	const { mutate: remove, isLoading: loadDelete } = useOrderDeliveryDelete(base);
	const { mutate: assign, isLoading: loadAssign } = useOrderDeliveryConfirmAssign(base);
	const { mutate: transform, isLoading: loadTransform } = useOrderDeliveryTransform(base);
	const { mutate: print, isLoading: loadPrint } = usePrintCodes();

	const { data: response } = useOrderDeliveryPaginate(base);
	const { permissions } = response;

	const navigate = useNavigate();

	let textTitle = textConstants[customer.country_id].delivery;
	if (location.pathname.includes("sin_documento")) {
		textTitle = "Documento";
	}

	const handleEdit = () => {
		if (location.pathname.includes("sin_documento")) {
			navigate(`/sin_documento/editar/${item.code}`);
		} else {
			navigate(`/guias_despacho/editar/${item.code}`);
		}
	};

	const handleDelete = () => {
		setShowDelete(false);

		const request = {
			base,
			element: item,
		};

		remove(request, {
			onSuccess: (_response) => {
				addToast(`${textTitle} eliminada correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	const handleAssign = (type) => {
		setShowAssignSale(false);
		setShowAssignDelivery(false);

		const request = {
			base,
			element: { ...item, type },
		};

		transform(request, {
			onSuccess: (response) => {
				addToast(`${textTitle} asignado correctamente.`, {
					appearance: "success",
				});

				if (response.type === "sale") {
					navigate(`/facturas/editar/${response.order_object.code}`);
				}

				if (response.type === "delivery") {
					navigate(`/guias_despacho/editar/${response.order_object.code}`);
				}
			},
		});
	};

	const handlePrint = () => {
		const allIds = data[indice]?.items.flatMap((item) => item.inventories.flatMap((inventory) => inventory.ids.split(",").map((id) => Number(id.trim()))));

		print(allIds, {
			onSuccess: (response) => {
				const blobUrl = URL.createObjectURL(base64toBlob(`data:application/pdf;base64,${response.url}`));

				printJS(blobUrl);
			},
		});
	};

	const handleViewDocument = () => {
		if (item.documents && item.documents.length > 0) {
			window.open(item.documents[0].url, "_blank");
		}
	};

	const items = [];

	if (hasPermission("edit", permissions)) {
		items.push({
			label: "Editar",
			action: handleEdit,
			icon: Edit,
		});
	}

	if (item.documents && item.documents.length > 0) {
		items.push({
			label: "Ver Documento",
			action: handleViewDocument,
			icon: FileText,
		});
	}

	if (location.pathname.includes("sin_documento")) {
		items.push(
			{
				label: "Asignar Factura",
				action: () => setShowAssignSale(true),
				icon: FileText,
			},
			{
				label: "Asignar Guía de Despacho",
				action: () => setShowAssignDelivery(true),
				icon: Truck,
			},
		);
	}

	items.push({
		label: "Imprimir Etiquetas",
		action: handlePrint,
		icon: QrCode,
	});

	if (hasPermission("delete", permissions)) {
		items.push({
			label: "Eliminar",
			action: () => setShowDelete(true),
			icon: Trash,
			danger: true,
		});
	}

	return (
		<>
			<CustomDropdown title="Acciones" items={items} align="right" isLoading={loadDelete || loadAssign || loadTransform || loadPrint} />

			<BasicConfirm
				subMessage={"Eliminar la guía de despacho retirará permanentemente los productos asociados del inventario y sus aplicaciones registradas."}
				message={"¿Está seguro de que desea eliminar la " + textTitle + " seleccionada?"}
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
				showValidation={true}
				textValidation={"eliminar la guia de despacho"}
			/>

			<BasicConfirm
				message={"¿Está seguro de que deseas asignar el documento a una factura?"}
				showConfirm={showAssignSale}
				setShowConfirm={setShowAssignSale}
				handleAction={() => handleAssign("sale")}
				showValidation={true}
				textValidation={"asignar la factura"}
			/>

			<BasicConfirm
				message={"¿Está seguro de que deseas asignar el documento a una guía de despacho?"}
				showConfirm={showAssignDelivery}
				setShowConfirm={setShowAssignDelivery}
				handleAction={() => handleAssign("delivery")}
				showValidation={true}
				textValidation={"asignar la guía de despacho"}
			/>

			<OrdersDeliveryAssign showAssign={showAssign} setShowAssign={setShowAssign} item={item} base={base} assign={assign} />
		</>
	);
};

export default OrdersDeliveryActionItem;
